import { Fragment, useState, FormEvent, SyntheticEvent, useMemo } from "react";
import { withBreaksAndLinks } from "utils/textUtils";
import Head from "next/head";
import { useCollection, useDocument } from "react-firebase-hooks/firestore";
import dayjs from "dayjs";
import Link from "next/link";
import { useRouter } from "next/router";
import relativeTime from "dayjs/plugin/relativeTime";
import { ArrowLeftIcon } from "@heroicons/react/24/solid";
import * as Sentry from "@sentry/browser";
dayjs.extend(relativeTime);

import { useAuth } from "context/AuthContext";
import { doc, query, collection, where, orderBy } from "firebase/firestore";
import { toast } from "react-toastify";
import { db } from "utils/firebase";
import { addComment } from "pages/api/comment";
import FullArticle from "./FullArticle";
import AudioPlayer from "./AudioPlayer";
import VideoPlayer from "./VideoPlayer";
import { Avatar, Comment, Loader } from "components/common";
import { getInitials } from "utils/textUtils";
import ImageView from "./ImageView";
import PostInteractions from "./PostInteractions";
import { handlePostLike } from "pages/api/like";
import {
  MAX_COMMENTS_PER_SECOND,
  MAX_LIKES_PER_SECOND,
  SOFT_LIMIT_COMMENTS_PER_SECOND,
  SOFT_LIMIT_LIKES_PER_SECOND,
} from "constants/hardLimits";
import NotFoundPost from "./NotFoundPost";
import { normalizeDate } from "utils/dateUtils";
import { Post } from "models/Post";
import { User } from "models/User";
import { BASE_URL } from "constants/redirects";
import PostPopup from "./PostPopup";
import { getProfileLink } from "utils/linkUtils";

interface ExpandedPostProps {
  backHref?: string;
  postData?: Post;
  postAuthorData?: User;
}

function ExpandedPost({
  backHref,
  postData,
  postAuthorData,
}: ExpandedPostProps) {
  const { user } = useAuth();
  const router = useRouter();
  const routerQuery = router.query;
  const postId = routerQuery?.postId?.toString() || "";
  const origin =
    typeof window !== "undefined" && window.location.origin
      ? window.location.origin
      : BASE_URL;

  const getPathname = () =>
    routerQuery?.profileId ? `/${routerQuery?.profileId}` : `/home`;

  // Temp hard limits
  const [firstLike, setFirstLike] = useState(null);
  const [firstComment, setFirstComment] = useState(null);
  const [numberOfLikes, setNumberOfLikes] = useState(0);
  const [numberOfComments, setNumberOfComments] = useState(0);

  const [postComment, setPostComment] = useState("");

  const normalizedPostCreatedAt = normalizeDate(postData?.createdAt);

  const [commentsCollection, commentsLoading] = useCollection(
    query(
      collection(db, "comments"),
      where("postId", "==", postId),
      orderBy("createdAt", "desc")
    )
  );

  const [likeDoc] = useDocument(doc(db, "likes", `${postId}_${user?.uid}`));

  const handleLikeClick = async () => {
    // Temp hard limits
    const now = new Date().getTime();

    if (!firstLike || now - 1000 >= firstLike) {
      setFirstLike(now);
      setNumberOfLikes(1);
    } else {
      if (numberOfLikes >= MAX_LIKES_PER_SECOND) {
        toast.error("`Please try again in 10 seconds.`");
        return false;
      } else {
        setNumberOfLikes((changes) => changes + 1);

        if (numberOfLikes >= SOFT_LIMIT_LIKES_PER_SECOND) {
          Sentry.captureMessage(`Soft limit likes reached - ${user.uid}`);
        }
      }
    }

    user
      ? await handlePostLike(postId, user?.uid || "")
      : router.push("/login");
  };

  const handlePostCommentChanges = (e: FormEvent<HTMLTextAreaElement>) =>
    setPostComment(e.currentTarget.value);

  const handlePostComment = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Temp hard limits
    const now = new Date().getTime();

    if (!firstComment || now - 1000 >= firstComment) {
      setFirstComment(now);
      setNumberOfComments(1);
    } else {
      if (numberOfComments >= MAX_COMMENTS_PER_SECOND) {
        toast.error("`Please try again in 10 seconds.`");
        return false;
      } else {
        setNumberOfLikes((changes) => changes + 1);

        if (numberOfLikes >= SOFT_LIMIT_COMMENTS_PER_SECOND) {
          Sentry.captureMessage(`Soft limit comments reached - ${user.uid}`);
        }
      }
    }

    setPostComment("");

    await addComment({
      details: {
        authorId: user?.uid || "",
        content: postComment,
        postId,
      },
    }).then(() => toast.success("Posted!"));
  };

  const hasChanges = () => !!postComment;

  const [postUpdate] = useDocument(doc(db, "posts", postId));
  const postUpdateData = postUpdate?.data();

  const renderComments = useMemo(() => {
    const parentComments = commentsCollection?.docs?.filter(
      (item) => !item?.data()?.parentId
    );

    return (
      <>
        {parentComments?.map((parentComment, index) => {
          if (parentComment) {
            const parentCommentData = parentComment.data();

            return (
              <Fragment key={parentComment.id}>
                <Comment
                  postId={postId}
                  commentId={parentComment.id}
                  authorId={parentCommentData?.authorId}
                  authorProfileImageUrl={parentCommentData?.profileImageUrl}
                  createdAt={normalizeDate(parentCommentData?.createdAt)}
                  content={parentCommentData?.content}
                />

                {commentsCollection?.docs?.map((childComment) => {
                  if (
                    childComment &&
                    childComment?.data()?.parentId === parentComment.id
                  ) {
                    const childCommentData = childComment.data();

                    return (
                      <Fragment key={childComment.id}>
                        <Comment
                          isChild
                          postId={postId}
                          commentId={childComment.id}
                          authorId={childCommentData.authorId}
                          authorProfileImageUrl={
                            childCommentData?.profileImageUrl
                          }
                          createdAt={normalizeDate(childCommentData?.createdAt)}
                          content={childCommentData?.content}
                        />
                      </Fragment>
                    );
                  } else return null;
                })}
                {parentComments?.length - 1 !== index ? (
                  <div className="mt-6 w-full border-t border-gray-300" />
                ) : null}
              </Fragment>
            );
          } else return null;
        })}
      </>
    );
  }, [commentsCollection?.docs, postId]);

  const renderPostContent = () => {
    if (!postData) return <NotFoundPost />;

    return (
      <>
        <div className="flex w-full flex-col items-center pb-4 lg:px-4">
          <div className="flex w-full max-w-3xl flex-col ">
            <div className="mb-4 flex items-start justify-center space-x-3">
              <div className="hidden flex-shrink-0 items-start justify-start sm:flex">
                <Link
                  href={getProfileLink(postAuthorData)}
                  className="rounded-full border border-transparent hover:border-orange-500"
                >
                  <Avatar
                    initials={getInitials(postAuthorData?.name)}
                    imageUrl={postAuthorData?.profileImageUrl}
                    size="l"
                  />
                </Link>
              </div>
              <div className="flex flex-shrink-0 items-start justify-start sm:hidden">
                <Link
                  href={getProfileLink(postAuthorData)}
                  className="rounded-full border border-transparent hover:border-orange-500"
                >
                  <Avatar
                    initials={getInitials(postAuthorData?.name)}
                    imageUrl={postAuthorData?.profileImageUrl}
                    size="m"
                  />
                </Link>
              </div>

              <div className="min-w-0 flex-1">
                <Link
                  className="text-md cursor-pointer whitespace-nowrap text-sm font-medium text-gray-900 hover:underline  sm:text-base"
                  href={getProfileLink(postAuthorData)}
                >
                  {postAuthorData?.name || "Anonymous"}
                </Link>
                <span className="mb-5 ml-4 text-sm text-gray-600">
                  {dayjs(normalizedPostCreatedAt).fromNow()}
                </span>

                {postData?.shortTextContent ? (
                  <p className="mb-5 mt-2 font-normal text-black">
                    {withBreaksAndLinks(postData?.shortTextContent)}
                  </p>
                ) : null}

                {postData?.audioSrc ? (
                  <div className="mt-5">
                    <AudioPlayer audioSrc={postData?.audioSrc} />
                  </div>
                ) : null}

                {postData?.videoSrc ? (
                  <div className="mt-5">
                    <VideoPlayer videoSrc={postData?.videoSrc} />
                  </div>
                ) : null}

                {postData?.imageSrc ? (
                  <div className="mt-5">
                    <ImageView imageSrc={postData?.imageSrc} />
                  </div>
                ) : null}

                {!postData?.articleContent && (
                  <PostInteractions
                    numberOfComments={postUpdateData?.commentCount}
                    onLikeClick={handleLikeClick}
                    isLiked={likeDoc?.exists()}
                    numberOfLikes={postUpdateData?.likeCount}
                    postUrl={`${origin}/posts/${postId}`}
                    shareTextContent={
                      postData?.shortTextContent ||
                      postData?.articleTitle ||
                      "Check this out!"
                    }
                  />
                )}
              </div>
            </div>

            {postData?.articleContent ? (
              <>
                <div className="mb-5">
                  <FullArticle
                    articleContent={postData?.articleContent}
                    articleTitle={postData?.articleTitle}
                  />
                </div>
                <PostInteractions
                  numberOfComments={postData?.commentCount}
                  onLikeClick={handleLikeClick}
                  isLiked={likeDoc?.exists()}
                  numberOfLikes={postData?.likeCount}
                  postUrl={`${origin}/posts/${postId}`}
                  shareTextContent={
                    postData?.shortTextContent ||
                    postData?.articleTitle ||
                    "Check this out!"
                  }
                />
              </>
            ) : null}
          </div>
        </div>

        <div className="mt-10 flex w-full max-w-2xl flex-col pb-16">
          <h4 className="text-xl">{`Comments (${
            postData?.commentCount || 0
          })`}</h4>
          <div className="mt-4 min-w-0 flex-1">
            <form
              action="#"
              className="relative"
              method="POST"
              onSubmit={handlePostComment}
            >
              <div className="overflow-hidden rounded-lg border border-gray-300 shadow-sm focus-within:border-orange-500 focus-within:ring-1 focus-within:ring-orange-500">
                <label htmlFor="comment" className="sr-only">
                  Add your comment
                </label>
                <textarea
                  rows={3}
                  name="comment"
                  id="comment"
                  className="block w-full resize-none border-0 py-3 focus:ring-0 sm:text-sm"
                  placeholder="Add your comment..."
                  value={postComment}
                  onChange={handlePostCommentChanges}
                />
              </div>

              <div className="mt-4 flex-shrink-0">
                {user ? (
                  <button
                    disabled={!hasChanges()}
                    type="submit"
                    className="inline-flex items-center rounded-md border border-transparent bg-orange-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-orange-600 disabled:opacity-70"
                  >
                    Post comment
                  </button>
                ) : (
                  <Link
                    href="/login"
                    className="inline-flex items-center rounded-md border border-transparent bg-orange-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-orange-600 disabled:opacity-70"
                  >
                    Post comment
                  </Link>
                )}
              </div>
            </form>
          </div>

          <div className="mt-10 flex w-full max-w-2xl">
            <div className="w-full">
              {!commentsLoading ? renderComments : <Loader />}
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="flex min-h-screen w-full flex-col items-center p-4">
      <div className="mb-8 flex w-full flex-row items-center justify-between">
        <div>
          <Link
            className="group flex justify-center rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none  focus:ring-orange-500 "
            href={backHref || "/"}
          >
            <span className="sr-only">Go back to feed</span>

            <ArrowLeftIcon
              className="m-2 h-6 w-6 group-focus:outline-none group-focus:ring-2 group-focus:ring-orange-500 group-focus:ring-offset-2"
              aria-hidden="true"
            />
          </Link>
        </div>

        {user?.uid === postData?.authorId ? (
          <PostPopup
            postId={postId}
            isPinned={!!postUpdateData?.pinned}
            isArticle={!!postUpdateData?.type.includes("article")}
            collection={postData?.collection}
          />
        ) : null}
      </div>

      {renderPostContent()}
    </div>
  );
}

export default ExpandedPost;
